import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useWebSocket from '../useWebSocket';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './NotificationDropdown.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const showSafeNotification = (title, options = {}) => {
  if (typeof window !== 'undefined' && 'Notification' in window) {
    if (Notification.permission === 'granted') {
      new Notification(title, options);
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(title, options);
        } else {
          toast.info(title);
        }
      });
    } else {
      toast.info(title);
    }
  } else {
    toast.info(title);
  }
};

const NotificationDropdown = ({ userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();

  const getWsUrl = (baseUrl, path) => {
    const url = new URL(baseUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = path;
    return url.toString();
  };

  const wsUrl = userId ? getWsUrl(BASE_URL, `/ws/notifications/${userId}/`) : null;

  const { connectionStatus, isConnected } = useWebSocket(wsUrl, {
    onMessage: (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'send_notification') {
        const newNotification = {
          id: data.notification_id,
          message: data.message,
          timestamp: new Date().toISOString(),
          read: false
        };
        setNotifications(prev => {
          if (prev.some(n => n.id === newNotification.id)) return prev;
          return [newNotification, ...prev];
        });
        setUnreadCount(prev => prev + (newNotification.read ? 0 : 1));
        showSafeNotification(data.message);
      }
    },
    reconnect: true,
    reconnectAttempts: 5,
    reconnectInterval: 3000
  });

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!userId) return;
      try {
        const response = await fetch(`${BASE_URL}/notification/notifications/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access')}`
          }
        });
        if (!response.ok) throw new Error('Failed to fetch notifications');
        const data = await response.json();
        const fetchedNotifications = data.results.map(notif => ({
          id: notif.id,
          message: notif.message,
          timestamp: notif.created_at,
          read: notif.is_read
        }));
        setNotifications(fetchedNotifications);
        setUnreadCount(fetchedNotifications.filter(notif => !notif.read).length);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotifications();
  }, [userId]);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = async () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      const unreadNotificationIds = notifications
        .filter(notif => !notif.read)
        .map(notif => notif.id);

      setNotifications(prev => prev.map(notif =>
        unreadNotificationIds.includes(notif.id) ? { ...notif, read: true } : notif
      ));
      setUnreadCount(0);

      try {
        const response = await fetch(`${BASE_URL}/notification/notifications/mark-read/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access')}`
          },
          body: JSON.stringify({ notification_ids: unreadNotificationIds })
        });
        if (!response.ok) {
          throw new Error('Failed to mark notifications as read');
        }
      } catch (error) {
        setNotifications(prev => prev.map(notif => ({
          ...notif,
          read: unreadNotificationIds.includes(notif.id) ? false : notif.read
        })));
        setUnreadCount(unreadNotificationIds.length);
      }
    }
  };

  const markAsRead = async (notificationId) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const response = await fetch(`${BASE_URL}/notification/notifications/mark-read/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access')}`
        },
        body: JSON.stringify({ notification_ids: [notificationId] })
      });
      if (!response.ok) throw new Error('Failed to mark notification as read');
    } catch (error) {
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: false } : notif
        )
      );
      setUnreadCount(prev => prev + 1);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <div className="notification-trigger" onClick={toggleDropdown}>
        <i className="fas fa-bell"></i>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
        <div className={`connection-status ${connectionStatus.toLowerCase()}`}
             title={`WebSocket: ${connectionStatus}`} />
      </div>

      {isOpen && (
        <div className="notification-dropdown-menu">
          <div className="notification-header">
            <h6>Notifications</h6>
          </div>
          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <i className="fas fa-bell-slash"></i>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="notification-content">
                    <p className="notification-message">{notification.message}</p>
                    <span className="notification-time">
                      {formatTime(notification.timestamp)}
                    </span>
                  </div>
                  {!notification.read && <div className="unread-indicator"></div>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
};

export default NotificationDropdown;
